/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useSnackbar } from "notistack";
import Papa from "papaparse";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import GetAppIcon from "@material-ui/icons/GetApp";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
//-ui/core/Tooltip";
//import InfoIcon from "@material-ui/icons/Info";

import Dialog from "../shared/Dialog";
import DialogActions from "../DialogActions";
import useModal from "../../hooks/useModal";
import { getListofusersContent, unhashEmails } from "../../api";
import InfoTooltip from "../InfoTooltip";
import { analyzeUsersListCsv } from "./usersListCsvUtils";

const TableCellFixed = styled(TableCell)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

// Shown but not editable
const isSystemField = (fieldName) => {
  if (!fieldName) return false;
  const normalized = String(fieldName).toLowerCase();
  // Add any other backend-only/system fields here
  return normalized === "existing_user";
};

const EditListContentModal = ({
  listId,
  listname,
  hasSnamCompany = false,
  hasCdpCompany = false,
  onClose,
  onConfirm,
  listData,
}) => {
  const { handleConfirm, isLoading: saving } = useModal({ onClose, onConfirm });
  const { enqueueSnackbar } = useSnackbar();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRecord, setNewRecord] = useState({});
  const [columns, setColumns] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: "asc",
  });
  const [isDirty, setIsDirty] = useState(false);
  const [fileUploadDisplayMessage, setFileUploadDisplayMessage] = useState(
    hasSnamCompany
      ? "Info: needs to have DID or email column"
      : hasCdpCompany? "Info: needs to have DID (populated with Microsoft oid)": "Info: needs to have DID"
  );

  const handleFileContentChecks = useCallback(
    (rows) => {
      try {
        const { data, errorMessage, infoMessage } = analyzeUsersListCsv(rows, {
          hasCdpCompany,
          hasSnamCompany,
        });

        if (errorMessage) {
          setFileUploadDisplayMessage(errorMessage);
          return;
        }

        if (data) {
          // Update content with uploaded rows
          setContent(data);

          // Recompute columns and newRecord based on uploaded data
          const allColumns = new Set();
          data.forEach((record) => {
            Object.keys(record).forEach((key) => allColumns.add(key));
          });
          const columnArray = Array.from(allColumns);
          setColumns(columnArray);

          const initialRecord = {};
          columnArray.forEach((col) => {
            if (!isSystemField(col)) {
              initialRecord[col] = "";
            }
          });
          setNewRecord(initialRecord);
          setIsDirty(true);

          setFileUploadDisplayMessage(infoMessage || `${data.length} records loaded`);
        }
      } catch (error) {
        console.error("Error in handleFileContentChecks => ", error);
        setFileUploadDisplayMessage("Could not load the file, or no data");
      }
    },
    [hasSnamCompany, hasCdpCompany]
  );

  const handleFileUpload = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (file) {
        Papa.parse(file, {
          complete: (results) => {
            handleFileContentChecks(results?.data);
          },
          header: true,
          skipEmptyLines: true,
        });
      }
    },
    [handleFileContentChecks]
  );

  const handleDownloadSampleFile = useCallback(() => {
    const isSnam = hasSnamCompany;

    let headers;
    let rows;

    if (isSnam) {
      // For SNAM: sample with only email column
      headers = ["email"];
      rows = [];
    } else {
      // For non-SNAM: sample with only DID column
      headers = ["DID"];
      rows = [
        ["c3e57852-c31f-4870-bc75-69148831483c"],
        ["26124244-d095-4f59-8382-e01e70c95ae9"],
      ];
    }

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join(
      "\n"
    );

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sample_users_list.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [hasSnamCompany]);

  const hasDidColumn = useMemo(
    () => columns.some((c) => c && c.toLowerCase() === "did"),
    [columns]
  );
  const hasEmailColumn = useMemo(
    () => columns.some((c) => c && c.toLowerCase() === "email"),
    [columns]
  );

  // Getting current list content
  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        // Refresh user existence information on open
        const data = await getListofusersContent(listId, true);

        if (data.fulldata && Array.isArray(data.fulldata)) {
          let processedContent = [...data.fulldata];

          if (data.fulldata.length > 0) {
            // Find the email column (case-insensitive)
            const firstRow = data.fulldata[0] || {};
            const emailColumnKey = Object.keys(firstRow).find(
              (key) => key && key.toLowerCase() === "email"
            );

            // Extract hashed emails from the email column
            if (emailColumnKey) {
              const hashedEmails = data.fulldata.map((record) => {
                const emailValue = record[emailColumnKey];
                return emailValue && typeof emailValue === "string" && emailValue.trim()
                  ? emailValue
                  : null;
              });

              // Filter to get only non-empty hashed emails for the API call
              const nonEmptyHashedEmails = hashedEmails.filter((email) => email !== null);

              // If we have hashed emails, unhash them
              if (nonEmptyHashedEmails.length > 0) {
                try {
                  const { plain } = await unhashEmails(nonEmptyHashedEmails);
                  if (plain && Array.isArray(plain) && plain.length === nonEmptyHashedEmails.length) {
                    // Replace hashed emails with plain emails in the same order
                    let plainEmailIndex = 0;
                    processedContent = data.fulldata.map((record) => {
                      const processedRecord = { ...record };
                      const emailValue = record[emailColumnKey];
                      if (
                        emailValue &&
                        typeof emailValue === "string" &&
                        emailValue.trim()
                      ) {
                        processedRecord[emailColumnKey] =
                          plain[plainEmailIndex] !== undefined
                            ? plain[plainEmailIndex]
                            : record[emailColumnKey];
                        plainEmailIndex += 1;
                      }
                      return processedRecord;
                    });
                  }
                } catch (error) {
                  console.error("Error unhashing emails:", error);
                  // Continue with hashed emails if unhashing fails
                }
              }
            }
          }

          setContent(processedContent);

          if (processedContent.length > 0) {
            const allColumns = new Set();
            processedContent.forEach((record) => {
              Object.keys(record).forEach((key) => allColumns.add(key));
            });
            const columnArray = Array.from(allColumns);
            setColumns(columnArray);

            // Initialize newRecord with empty strings for non-system columns
            const initialRecord = {};
            columnArray.forEach((col) => {
              if (!isSystemField(col)) {
                initialRecord[col] = "";
              }
            });
            setNewRecord(initialRecord);
          }
          setIsDirty(false);
        }
      } catch (error) {
        console.error("Error fetching content:", error);
        enqueueSnackbar("Error fetching list content", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };

    if (listId) {
      fetchContent();
    }
  }, [listId, enqueueSnackbar]);

  const handleAddRecord = useCallback(() => {
    // Check if DID is provided
    const hasDID = newRecord.DID && newRecord.DID.trim();

    // Check if any email column (case-insensitive) is provided
    let hasEmailValue = false;
    for (const key in newRecord) {
      if (
        key.toLowerCase() === "email" &&
        newRecord[key] &&
        newRecord[key].trim()
      ) {
        hasEmailValue = true;
        break;
      }
    }

    if (hasSnamCompany) {
      // For Snam:
      // - If both DID and Email columns exist, BOTH must be provided
      // - If only Email exists, Email is required
      // - If only DID exists, DID is required
      if (hasDidColumn && hasEmailColumn) {
        if (!hasDID || !hasEmailValue) {
          enqueueSnackbar("DID and Email are required", { variant: "error" });
          return;
        }
      } else if (!hasDidColumn && hasEmailColumn) {
        if (!hasEmailValue) {
          enqueueSnackbar("Email is required", { variant: "error" });
          return;
        }
      } else if (hasDidColumn && !hasEmailColumn) {
        if (!hasDID) {
          enqueueSnackbar("DID is required", { variant: "error" });
          return;
        }
      } else {
        enqueueSnackbar("Identifier is required", { variant: "error" });
        return;
      }
    } else if (hasCdpCompany) {
      if (hasEmailColumn) {
        enqueueSnackbar("Only DID column is allowed", { variant: "error" });
        return;
      }
      if (!hasDID) {
        enqueueSnackbar("DID is required", { variant: "error" });
        return;
      }
    } else {
      // For non-Snam companies: DID is mandatory
      if (!hasDID) {
        enqueueSnackbar("DID is required", { variant: "error" });
        return;
      }
    }

    // Create a new record with all non-system columns, using empty string for empty fields
    const fullRecord = {};
    columns.forEach((col) => {
      if (!isSystemField(col)) {
        fullRecord[col] = newRecord[col] || "";
      }
    });

    setContent((prev) => [...prev, fullRecord]);
    setIsDirty(true);

    // Clear only the input fields
    const clearedRecord = {};
    columns.forEach((col) => {
      if (!isSystemField(col)) {
        clearedRecord[col] = "";
      }
    });
    setNewRecord(clearedRecord);
  }, [
    newRecord,
    columns,
    enqueueSnackbar,
    hasSnamCompany,
    hasCdpCompany,
    hasDidColumn,
    hasEmailColumn,
  ]);

  const handleDeleteRecord = useCallback(
    (record) => () => {
      setContent((prev) => prev.filter((r) => r !== record));
      setIsDirty(true);
    },
    []
  );

  const handleFieldChange = useCallback(
    (field) => (e) => {
      const value = e.target.value;
      setNewRecord((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    if (content.length === 0) {
      enqueueSnackbar("List cannot be empty", { variant: "error" });
      return;
    }

    // Validate that each record has the required identifier(s)
    const invalidRecords = content.filter((record) => {
      const hasDID = record.DID && record.DID.trim();
      let hasEmail = false;
      for (const key in record) {
        if (key.toLowerCase() === "email" && record[key] && record[key].trim()) {
          hasEmail = true;
          break;
        }
      }

      if (hasSnamCompany) {
        // For Snam:
        // - If both DID and Email columns exist, BOTH must be present
        // - If only Email exists, Email is required
        // - If only DID exists, DID is required
        if (hasDidColumn && hasEmailColumn) {
          return !hasDID || !hasEmail;
        }
        if (!hasDidColumn && hasEmailColumn) {
          return !hasEmail;
        }
        if (hasDidColumn && !hasEmailColumn) {
          return !hasDID;
        }
        // Fallback: require at least one identifier
        return !hasDID && !hasEmail;
      }

      if (hasCdpCompany) {
        return !hasDID;
      }

      // For non-Snam: DID is required
      return !hasDID;
    });

    if (hasCdpCompany && !hasSnamCompany && hasEmailColumn) {
      enqueueSnackbar("Only DID column is allowed", { variant: "error" });
      return;
    }

    if (invalidRecords.length > 0) {
      let message;
      if (hasSnamCompany) {
        if (hasDidColumn && hasEmailColumn) {
          message = "missing required identifiers (DID and Email)";
        } else if (!hasDidColumn && hasEmailColumn) {
          message = "missing required identifier (Email)";
        } else if (hasDidColumn && !hasEmailColumn) {
          message = "missing required identifier (DID)";
        } else {
          message = "missing required identifier";
        }
      } else {
        message = "missing required identifier (DID)";
      }

      enqueueSnackbar(
        `${invalidRecords.length} record(s) ${message}`,
        { variant: "error" }
      );
      return;
    }

    // Strip the existing_user field as it's added by the backend
    const cleanedContent = content.map((record) => {
      const { existing_user, ...rest } = record;
      if (hasCdpCompany && !hasSnamCompany) {
        return {
          DID: record.DID?.trim() || "",
        };
      }
      return rest;
    });

    try {
      await handleConfirm({ fulldata: cleanedContent });
      enqueueSnackbar("List content updated successfully", {
        variant: "success",
      });
      setIsDirty(false);
    } catch (error) {
      enqueueSnackbar("Error updating list content", { variant: "error" });
    }
  }, [
    content,
    handleConfirm,
    enqueueSnackbar,
    hasSnamCompany,
    hasCdpCompany,
    hasDidColumn,
    hasEmailColumn,
  ]);

  const handleSearchChange = useCallback((e) => {
    setSearchText(e.target.value);
  }, []);

  // By default sorting by the first column in ascending order
  useEffect(() => {
    if (!sortConfig.column && columns.length) {
      const firstCol = columns[0];
      if (firstCol) {
        setSortConfig({ column: firstCol, direction: "asc" });
      }
    }
  }, [columns, sortConfig.column]);

  const handleSort = useCallback((column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return {
          column,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { column, direction: "asc" };
    });
  }, []);

  const displayedContent = useMemo(() => {
    let rows = content;

    if (searchText.trim()) {
      const query = searchText.trim().toLowerCase();
      rows = rows.filter((row) =>
        columns.some((col) => {
          const value = row[col];
          return (
            value &&
            String(value)
              .toLowerCase()
              .includes(query)
          );
        })
      );
    }

    if (sortConfig.column) {
      const { column, direction } = sortConfig;
      const multiplier = direction === "asc" ? 1 : -1;
      rows = [...rows].sort((a, b) => {
        const aVal = a[column] ?? "";
        const bVal = b[column] ?? "";
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();

        if (aStr < bStr) return -1 * multiplier;
        if (aStr > bStr) return 1 * multiplier;
        return 0;
      });
    } else {
      rows = [...rows];
    }

    return rows;
  }, [content, columns, searchText, sortConfig]);

  if (loading) {
    return (
      <Dialog fullWidth open maxWidth="xl" onClose={onClose}>
        <DialogTitle>Edit List Content: {listname}</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog fullWidth open maxWidth="xl" onClose={onClose}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between">
          <Box alignItems="center" display="flex">
            <Typography variant="h6">Edit List Content: {listname}</Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box mt={2}>
          <Box mb={1}>
            <Typography gutterBottom variant="subtitle1">
              Overwrite the list with new csv
            </Typography>
          </Box>
          <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
            mt={2}
          >
            <Box alignItems="center" display="flex">
              <input
                accept=".csv"
                id="csv-file-upload-content"
                style={{ display: "none" }}
                type="file"
                onChange={handleFileUpload}
              />
              <Box
                alignItems="center"
                display="inline-flex"
                position="relative"
              >
                <label htmlFor="csv-file-upload-content">
                  <Button
                    color="primary"
                    component="span"
                    endIcon={<InsertDriveFileIcon />}
                    variant="outlined"
                  >
                    Upload CSV File
                  </Button>
                </label>
                <Box position="absolute" right={-18} top={-18}>
                  <InfoTooltip
                    text="Uploading a new file will overwrite the current list content in this modal"
                    title="Upload behavior"
                  />
                </Box>
              </Box>
              <Box
                alignItems="center"
                display="inline-flex"
                ml={6}
                position="relative"
              >
                <Button
                  color="primary"
                  endIcon={<GetAppIcon />}
                  variant="outlined"
                  onClick={handleDownloadSampleFile}
                >
                  Download Sample File
                </Button>
                <Box position="absolute" right={-18} top={-18}>
                  <InfoTooltip
                    text={
                      hasSnamCompany
                        ? "CSV must contain a DID column or an email column or both."
                        : hasCdpCompany
                        ? "CSV must contain only a DID column (populated with Microsoft oid)."
                        : "CSV must contain a DID column."
                    }
                    title="CSV requirements"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          {fileUploadDisplayMessage && (
            <Box mt={1}>
              <Typography color="textSecondary" variant="body2">
                {fileUploadDisplayMessage}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Add Record Section */}
          <Grid item xs={12}>
            <Box mb={2}>
              <Typography gutterBottom variant="subtitle1">
                Add New Record
              </Typography>
              <Grid container alignItems="flex-start" spacing={1}>
                {columns
                  .filter((col) => !isSystemField(col))
                  .map((col, index) => {
                    const lowerCol = col.toLowerCase();
                    let helperText;

                    if (hasSnamCompany) {
                      // For Snam:
                      // - If both DID and Email columns exist, both are required
                      // - If only Email exists (no DID), Email is required
                      if (hasDidColumn && hasEmailColumn && index === 0) {
                        helperText = "*DID and Email are required";
                      } else if (
                        !hasDidColumn &&
                        hasEmailColumn &&
                        lowerCol === "email"
                      ) {
                        helperText = "*Email is required";
                      }
                    } else {
                      // For non-Snam: DID is required (hint under DID only)
                      if (lowerCol === "did") {
                        helperText = "*DID is required";
                      }
                    }
                    return (
                      <Grid key={col} item lg={3} md={4} sm={6} xs={12}>
                        <TextField
                          fullWidth
                          helperText={helperText}
                          label={col}
                          size="small"
                          value={newRecord[col] || ""}
                          variant="outlined"
                          onChange={handleFieldChange(col)}
                        />
                      </Grid>
                    );
                  })}
                <Grid item lg={3} md={4} sm={6} xs={12}>
                  <Button
                    fullWidth
                    color="primary"
                    startIcon={<AddIcon />}
                    variant="contained"
                    onClick={handleAddRecord}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Records Table */}
          <Grid item xs={12}>
            <Box alignItems="center" display="flex" justifyContent="space-between" mb={1}>
              <Typography gutterBottom variant="subtitle1">
                Records ({content.length})
              </Typography>
              <TextField
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                placeholder="Search..."
                size="small"
                value={searchText}
                variant="outlined"
                onChange={handleSearchChange}
              />
            </Box>
            <TableContainer component={Paper} style={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((col) => {
                      const isExistingUserCol = col === "existing_user";
                      //const showExistenceInfo = isExistingUserCol;
                      return (
                        <TableCell
                          key={col}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort(col)}
                        >
                          <Box alignItems="center" display="flex">
                            <strong>
                              {isExistingUserCol ? "User exists?" : col}
                            </strong>
                            {/* {showExistenceInfo && (
                              <Box component="span" ml={0.5} mt={0.75}>
                                <Tooltip title="The existence check is performed only when there is a valid DID value.">
                                  <InfoIcon
                                    color="action"
                                    fontSize="small"
                                    style={{ cursor: "default" }}
                                  />
                                </Tooltip>
                              </Box>
                            )} */}
                            {sortConfig.column === col && (
                              <Box display="flex" ml={0.5}>
                                {sortConfig.direction === "asc" ? (
                                  <ArrowUpwardIcon fontSize="small" />
                                ) : (
                                  <ArrowDownwardIcon fontSize="small" />
                                )}
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                    <TableCell width="100">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedContent.map((row, index) => (
                    <TableRow key={index}>
                      {columns.map((col) => {
                        const value = row[col];
                        const isExistingUserCol = col === "existing_user";

                        let displayValue;
                        if (isExistingUserCol) {
                          // Show empty for newly added records (no existing_user value yet)
                          if (value === "yes") {
                            displayValue = "Yes";
                          } else if (value === "no" || value === "unknown") {
                            displayValue = "No";
                          } else {
                            displayValue = "-";
                          }
                        } else {
                          displayValue = value || "";
                        }

                        return (
                          <TableCellFixed
                            key={col}
                            title={displayValue}
                          >
                            {displayValue}
                          </TableCellFixed>
                        );
                      })}
                      <TableCell>
                        <IconButton
                          color="secondary"
                          size="small"
                          onClick={handleDeleteRecord(row)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          disabled={loading || saving || !isDirty}
          endIcon={(loading || saving) && <CircularProgress size={25} />}
          variant="contained"
          onClick={handleSubmit}
        >
          Save Changes
        </Button>
        <Button
          color="primary"
          disabled={loading || saving}
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditListContentModal;
